import React from "react";
import { Alert, Box, CircularProgress, Grid, Typography, useTheme } from "@mui/material";

import { DataLayout, DonutChart } from "../../../shared/components";

import { useCriticalRisks, useRiskCategories, useRiskMatrix, useRiskSummary } from "../mutation";

import { RiskCard } from "./RiskCard";
import { RiskHeatmap } from "./RiskHeatmap";
import { RiskDetailsCard } from "./RiskDetailsCard";

export const RiskOverView: React.FC<{ projectId: string | undefined }> = ({ projectId }) => {

    const theme = useTheme();

    const { isPending: isCriticalRisksPending, criticalRiskData, isError: isCriticalRisksError, error: criticalRisksError } = useCriticalRisks(projectId);
    const { isPending: isRiskCategoriesPending, riskCategoryData, isError: isRiskCategoriesError, error: riskCategoriesError } = useRiskCategories(projectId);
    const { isPending: isRiskMatrixPending, riskMatrixData, isError: isRiskMatrixError, error: riskMatrixError } = useRiskMatrix(projectId);
    const { isPending: isRiskSummaryPending, riskSummaryData, isError: isRiskSummaryError, error: riskSummaryError } = useRiskSummary(projectId);

    const colorPalette = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.info.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
    ];

    const getRandomColor = (index: number) => {
        return colorPalette[index % colorPalette.length];
    };

    const chartData = riskCategoryData.map((item: { category_percentage: string; category: string; }, index: any) => ({
        id: index,
        label: item.category,
        value: parseInt(item.category_percentage, 10) || 0,
        color: getRandomColor(index),
        legendLabel: item.category?.concat(` ${item.category_percentage}%`),
        legendValue: `${item.category_percentage}%`
    }));

    if (isRiskSummaryPending || isRiskCategoriesPending || isCriticalRisksPending || isRiskMatrixPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if (isCriticalRisksError || isRiskCategoriesError || isRiskMatrixError || isRiskSummaryError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                <Alert
                    severity="warning"
                    sx={{
                        width: "auto",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    {criticalRiskData?.message || riskCategoryData?.message || riskMatrixData?.message || riskSummaryData?.message || "Something went wrong!"}
                </Alert>
            </Box>
        );
    };


    return (
        <Box p={2} display="flex" flexDirection="column" gap={2}>
            <Grid container spacing={2}>
                <Grid size={4}>
                    <RiskCard title={'Total Risks'} value={riskSummaryData?.total_risk || 0} status={'error'} />
                </Grid>
                <Grid size={4}>
                    <RiskCard title={'Open Risks'} value={riskSummaryData?.open_risk_count || 0} status={'warning'} />
                </Grid>
                <Grid size={4}>
                    <RiskCard title={'Resolution Rate'} value={`${riskSummaryData?.resolution_rate || 0}%`} status={'success'} />
                </Grid>
            </Grid>
            <Box display={'flex'} alignItems={'stretch'} gap={2}>
                <DataLayout title="Risk Categories">
                    <Box
                        display="flex"
                        gap={2}
                        flexDirection="column"
                        p={2}
                        sx={{
                            height: 250,
                            overflowY: 'auto'
                        }}
                    >
                        {
                            riskCategoryData.length === 0 ? (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    alignContent="center"
                                    width="100%"
                                    height="100%"
                                    gap={2}
                                    mt={5}
                                >
                                    <Typography variant="body2">No risk categories data available.</Typography>
                                </Box>
                            ) :
                                <Box height={250}>
                                    <DonutChart data={chartData} />
                                </Box>
                        }
                    </Box>
                </DataLayout>
                <DataLayout title="Critical Risks">
                    <Box
                        display="flex"
                        gap={2}
                        flexDirection="column"
                        p={2}
                        sx={{
                            height: 250,
                            overflowY: 'auto'
                        }}
                    >
                        {
                            criticalRiskData.length === 0 ? (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    alignContent="center"
                                    width="100%"
                                    height="100%"
                                    gap={2}
                                    mt={5}
                                >
                                    <Typography variant="body2">No critical risks found.</Typography>
                                </Box>
                            ) :
                                criticalRiskData.map((risk: {
                                    project_ref: string,
                                    title: string,
                                    category: string,
                                    risk_rag: string
                                }, index: React.Key) => (
                                    <RiskDetailsCard risk={risk} key={index} />
                                ))
                        }
                    </Box>
                </DataLayout>
            </Box>
            <DataLayout title="Risk Matrix">
                <RiskHeatmap riskMatrixDetails={riskMatrixData} />
            </DataLayout>
        </Box>
    )

};